import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { isPlatformApiError } from '@traceio-org/platform-client';
import { PRODUCTS, hubUrl, } from '@traceio-org/platform-contracts';
import { PRODUCT_TAGLINES } from '../brand/lockups.js';
import { traceButtonClass } from '../components/cx.js';
import { SigningIn, signingInTitle } from '../components/SigningIn.js';
import { LoginShell } from '../components/LoginShell.js';
import { ProductLockup } from '../components/ProductLockup.js';
import { StatusView } from '../components/StatusView.js';
import { WorkspaceList } from '../components/WorkspaceList.js';
import { DestinationCheckError, ensureDestinationAccess, leaveForHub, recordHubTrip, takeHandedOrganization, } from './ensure-access.js';
const DestinationAccessContext = createContext(null);
/** How long one access check may run before the gate abandons it and shows the failed check (Try again). */
export const DESTINATION_CHECK_TIMEOUT_MS = 15_000;
/** The longest delay a timer holds (2^31 - 1 milliseconds, about 24.8 days); a longer one fires at once. */
const MAX_TIMER_MS = 2 ** 31 - 1;
/**
 * The time limit of a check: checkTimeoutMs when it is a whole number of milliseconds from 1 to 2^31 - 1, the
 * default otherwise. Risky by nature: setTimeout fires at once for 0, a negative value, NaN, Infinity or
 * anything above the largest timer, which would fail every check before it could answer.
 */
function checkTimeLimit(value) {
    return value !== undefined && Number.isInteger(value) && value >= 1 && value <= MAX_TIMER_MS
        ? value
        : DESTINATION_CHECK_TIMEOUT_MS;
}
/** The access DestinationGate confirmed: person, product, context (organization and roles, or staff role). */
export function useDestination() {
    const access = useContext(DestinationAccessContext);
    if (!access) {
        throw new Error('useDestination() works only inside DestinationGate, once access is confirmed');
    }
    return access;
}
const SIGN_IN_FAILURES = {
    no_session: {
        title: (product) => `We could not sign you in to ${product}`,
        body: (product) => `Signing in through Trace did not reach ${product}. Sign in again through Trace to continue.`,
    },
    reauthenticate_loop: {
        title: (product) => `We could not confirm the sign-in ${product} requires`,
        body: () => 'Sign in again through Trace with the sign-in method of your organization, or with multi-factor authentication when it asks for it.',
    },
    session_rejected: {
        title: () => 'Your session was not accepted',
        body: (product) => `Trace did not accept your session for ${product}. It may have ended. Sign in again through Trace.`,
    },
    access_denied: {
        title: () => 'Sign-in did not finish',
        body: () => 'The sign-in was cancelled, or your account was not given access. You can sign in again through Trace.',
    },
    login_required: {
        title: () => 'Sign in again',
        body: () => 'Your sign-in session ended before it finished.',
    },
    invalid_state: {
        title: () => 'This sign-in link has expired',
        body: () => 'A sign-in link works once, in the browser that started it. Sign in again through Trace.',
    },
    network: {
        title: () => 'We could not reach the sign-in service',
        body: () => 'Check your connection, then sign in again through Trace.',
    },
    organization_mismatch: {
        title: () => 'Sign-in did not finish',
        body: () => 'You signed in to a different organization than the one this page asked for. Sign in again through Trace.',
    },
    unknown: {
        title: () => 'Sign-in did not finish',
        body: () => 'Something went wrong while completing sign-in. Sign in again through Trace.',
    },
};
function hubHome(hubOrigin) {
    try {
        return hubUrl(hubOrigin, { route: 'login' });
    }
    catch {
        return null;
    }
}
/** Moves focus to the view's heading when it appears, and names the browser tab after it. */
function useHeading(title) {
    const ref = useRef(null);
    useEffect(() => {
        document.title = `${title} | Trace`;
        ref.current?.focus({ preventScroll: true });
    }, [title]);
    return ref;
}
function GateFrame({ productKey, children }) {
    return (_jsx(LoginShell, { brand: _jsx(ProductLockup, { productKey: productKey, surface: "dark" }), tagline: PRODUCT_TAGLINES[productKey], children: children }));
}
function Progress({ title }) {
    const headingRef = useHeading(title);
    return _jsx(SigningIn, { title: title, headingRef: headingRef });
}
function ChooseWorkspace({ product, person, contexts, assetBaseUrl, onSelect, }) {
    const headingRef = useHeading('Choose a workspace');
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "trace-auth-header", children: [_jsx("h1", { ref: headingRef, tabIndex: -1, className: "trace-auth-title", children: "Choose a workspace" }), person.email ? _jsxs("p", { className: "trace-auth-lead", children: ["Signed in as ", person.email, "."] }) : null] }), _jsxs("p", { className: "trace-auth-muted", children: ["Workspaces for ", product] }), _jsx(WorkspaceList, { contexts: contexts, onSelect: onSelect, assetBaseUrl: assetBaseUrl })] }));
}
function SignInFailed({ product, code, signInUrl, onLeave, }) {
    const copy = SIGN_IN_FAILURES[code];
    const title = copy.title(product);
    const headingRef = useHeading(title);
    return (_jsx(StatusView, { tone: "warning", title: title, headingRef: headingRef, primaryAction: 
        // A plain link (it opens the hub, nothing else); the click records the trip for the way back.
        _jsx("a", { className: traceButtonClass('primary', { block: true }), href: signInUrl, onClick: onLeave, children: "Sign in through Trace" }), children: _jsx("p", { children: copy.body(product) }) }));
}
function CheckFailed({ product, error, home, onRetry, }) {
    const title = `We could not check your access to ${product}`;
    const headingRef = useHeading(title);
    const reference = isPlatformApiError(error) ? error.correlationId : null;
    return (_jsx(StatusView, { tone: "error", title: title, headingRef: headingRef, primaryAction: { label: 'Try again', onClick: onRetry }, secondaryAction: home ? { label: 'Go to the Trace account hub', href: home } : null, footer: reference ? _jsxs("span", { children: ["Support reference: ", reference] }) : null, children: _jsxs("p", { children: ["Trace could not confirm that you may use ", product, ", so nothing was opened. Check your connection, then try again."] }) }));
}
/**
 * The access gate every Trace destination wraps around itself (Trace Admin, the navigation fixtures, the
 * products). It runs ensureDestinationAccess once per mount, shows SigningIn while it runs, and renders
 * its children only once access is confirmed (by platform-api, or by the product backend through
 * checkAccess); useDestination() then gives them that access.
 * Everything else ends in exactly one of: the hub sign-in, the hub page that explains a denial (or
 * renderDenied), a workspace choice, a recoverable sign-in failure, or a failed check with Try again.
 */
export function DestinationGate(props) {
    const { productKey, session, client, hubOrigin, audience, platformAudience, checkAccess, claimNamespace, renderDenied, children, checkTimeoutMs, assign, storage, now, } = props;
    const timeLimit = checkTimeLimit(checkTimeoutMs);
    const [view, setView] = useState({ kind: 'checking' });
    const [run, setRun] = useState({
        attempt: 0,
    });
    // One check per attempt. React StrictMode runs effects twice on mount; the second run must not repeat
    // a check that completes a sign-in, records an attempt or starts a redirect.
    const started = useRef(-1);
    // The organization the hub handed over in the address, taken out of it by the first attempt and kept for every
    // attempt (Try again included), since the address no longer has it. Read once, null included: an address
    // written after the page opened (by the application, a link inside it) is never read as a handed organization.
    const handedRead = useRef(false);
    const handedOrganization = useRef(null);
    // The running attempt (null once it settled): aborted by the next attempt, on unmount and when its time is up.
    const attempt = useRef(null);
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            // StrictMode unmounts and mounts again at once in development: only a real unmount (or an <Activity> that
            // hides the gate) is still unmounted when the microtask runs, and only that aborts the running check.
            queueMicrotask(() => {
                if (mounted.current || !attempt.current)
                    return;
                attempt.current.abort();
                attempt.current = null;
                // React 19.2 <Activity> hides and shows a subtree by destroying and re-creating its effects without a
                // remount: state and refs survive. The aborted attempt never answers, so it must not count as started,
                // or the re-created effect starts none and the gate shows "Checking your access" forever. A check that
                // already settled is not repeated (attempt.current is null then).
                started.current = -1;
            });
        };
    }, []);
    useEffect(() => {
        if (started.current === run.attempt)
            return;
        started.current = run.attempt;
        if (!handedRead.current) {
            handedRead.current = true;
            handedOrganization.current = takeHandedOrganization();
        }
        attempt.current?.abort();
        const controller = new AbortController();
        attempt.current = controller;
        // Risky by nature (a check that never ends shows a spinner forever), so the gate does not rely on the hook
        // or the session honoring the signal: whichever comes first, the check's outcome or the abort, decides.
        const timer = setTimeout(() => controller.abort(new DestinationCheckError('timed_out')), timeLimit);
        const aborted = new Promise((_resolve, reject) => {
            controller.signal.addEventListener('abort', () => reject(controller.signal.reason), { once: true });
        });
        const current = () => mounted.current && attempt.current === controller;
        const navigation = {
            ...(storage !== undefined ? { storage } : {}),
            ...(now ? { now } : {}),
            ...(assign ? { assign } : {}),
        };
        const apply = (outcome) => {
            switch (outcome.kind) {
                case 'granted':
                    setView({ kind: 'granted', access: outcome.access });
                    return;
                case 'sign-in':
                    setView({ kind: 'leaving', to: 'sign-in' });
                    leaveForHub(outcome.hubUrl, navigation);
                    return;
                case 'redirecting':
                    setView({ kind: 'leaving', to: 'provider' });
                    return;
                case 'denied':
                    if (renderDenied) {
                        setView({ kind: 'denied', denial: outcome.denial });
                        return;
                    }
                    setView({ kind: 'leaving', to: 'hub' });
                    if (outcome.denial.kind === 'reauthenticate') {
                        leaveForHub(outcome.denial.hubUrl, { ...navigation, reauthenticate: outcome.denial.reason });
                    }
                    else {
                        (assign ?? ((url) => window.location.assign(url)))(outcome.denial.hubUrl);
                    }
                    return;
                case 'choose-workspace':
                    setView({ kind: 'choose', person: outcome.person, contexts: outcome.contexts });
                    return;
                case 'sign-in-failed':
                    setView({ kind: 'sign-in-failed', code: outcome.code, hubUrl: outcome.hubUrl });
                    return;
                case 'error':
                    setView({ kind: 'error', error: outcome.error });
                    return;
            }
        };
        void Promise.race([
            ensureDestinationAccess({
                productKey,
                session,
                client,
                hubOrigin,
                audience,
                ...(platformAudience ? { platformAudience } : {}),
                ...(checkAccess ? { checkAccess } : {}),
                ...(claimNamespace ? { claimNamespace } : {}),
                ...(run.contextId ? { contextId: run.contextId } : {}),
                ...(run.contextOrganization ? { contextOrganization: run.contextOrganization } : {}),
                signInOrganization: handedOrganization.current,
                ...(storage !== undefined ? { storage } : {}),
                ...(now ? { now } : {}),
                signal: controller.signal,
            }),
            aborted,
        ])
            .then((outcome) => {
            if (current())
                apply(outcome);
        }, 
        // The time limit's DestinationCheckError (or any failure) is the failed check with Try again; an
        // attempt that was replaced or unmounted shows nothing.
        (error) => {
            if (current())
                setView({ kind: 'error', error });
        })
            .finally(() => {
            clearTimeout(timer);
            // Settled: nothing left to abort (an <Activity> that hides the gate now does not start it again).
            if (attempt.current === controller)
                attempt.current = null;
        });
    }, [
        run,
        productKey,
        session,
        client,
        hubOrigin,
        audience,
        platformAudience,
        checkAccess,
        claimNamespace,
        renderDenied,
        timeLimit,
        assign,
        storage,
        now,
    ]);
    const recheck = (contextId, contextOrganization) => {
        setView({ kind: 'checking' });
        setRun((current) => ({
            attempt: current.attempt + 1,
            ...(contextId ? { contextId } : {}),
            ...(contextOrganization ? { contextOrganization } : {}),
        }));
    };
    const product = PRODUCTS[productKey].displayName;
    switch (view.kind) {
        case 'granted':
            return (_jsx(DestinationAccessContext.Provider, { value: view.access, children: children }));
        case 'denied':
            return _jsx(_Fragment, { children: renderDenied ? renderDenied(view.denial) : null });
        // Checking and leaving (for Trace sign-in, the provider or the account hub) are steps of one sign-in: one
        // Progress element with one title and no step text, the same screen the hub's pages and the static index.html
        // shell show, so crossing pages never changes what is on screen (a step line read as a loop, dev 2026-10-07).
        case 'checking':
        case 'leaving':
            return (_jsx(GateFrame, { productKey: productKey, children: _jsx(Progress, { title: signingInTitle(product) }) }));
        case 'choose':
            return (_jsx(GateFrame, { productKey: productKey, children: _jsx(ChooseWorkspace, { product: product, person: view.person, contexts: view.contexts, assetBaseUrl: client.baseUrl, onSelect: (contextId) => {
                        // An Auth0 session signs in to the chosen workspace's organization for its token.
                        const chosen = view.contexts.find((c) => c.id === contextId);
                        recheck(contextId, chosen?.type === 'employee' ? chosen.organizationHint : undefined);
                    } }) }));
        case 'sign-in-failed':
            return (_jsx(GateFrame, { productKey: productKey, children: _jsx(SignInFailed, { product: product, code: view.code, signInUrl: view.hubUrl, onLeave: () => recordHubTrip({ ...(storage !== undefined ? { storage } : {}), ...(now ? { now } : {}) }) }) }));
        case 'error':
            return (_jsx(GateFrame, { productKey: productKey, children: _jsx(CheckFailed, { product: product, error: view.error, home: hubHome(hubOrigin), onRetry: () => recheck(run.contextId, run.contextOrganization) }) }));
    }
}
//# sourceMappingURL=DestinationGate.js.map